import { Button } from '@tunasong/ui-lib'
import { RecordFabButton } from '@tunasong/audio-ui'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Add } from '@tunasong/icons'
import { EntityFilters } from '@tunasong/models'
import { getAcceptedMimeTypes, usePlugins } from '@tunasong/plugin-lib'
import { isFolderContainer } from '@tunasong/schemas'
import { useSearch } from '@tunasong/search'
import {
  ContentContainer,
  DndList,
  DropZone,
  EntityGrid,
  Typography,
  useNavigateToEntity,
} from '@tunasong/ui-lib'
import { useCurrentUser } from '@tunasong/redux'
import cn from 'classnames'
import type { FC, MouseEvent } from 'react'
import { RecentActivity } from './recent-activity.js'
import styles from './root-main.module.css'

export interface RootMainProps {}

export const RootMain: FC<RootMainProps> = props => {
  const {} = props
  const { userId } = useCurrentUser()

  const navigateToEntity = useNavigateToEntity()

  const { results: sharedSearchResults, searching } = useSearch({ queryType: 'shared', query: userId, size: 500 })
  /** Filter out results that has a parentId as well */
  const sharedResultIds = new Set([...sharedSearchResults.map(r => r.id)])

  const { entities: ownedEntities, isLoading: isLoadingEntities } = graphHooks.useEntities({
    root: true,
    filter: EntityFilters.folder,
  })
  const ownedResultIds = new Set([...ownedEntities.map(r => r.id)])

  const sharedResults = sharedSearchResults
    .filter(r => (r.parentId ? !sharedResultIds.has(r.parentId) : true))
    .filter(r => !ownedResultIds.has(r.id))

  const allEntities = [...ownedEntities, ...sharedResults]

  const spaces = allEntities
    // Only show top-level entities
    .filter(e => !Boolean(e.parentId) || e.parentId === 'ROOT')
    .filter(isFolderContainer)
    // Filter out spaces that are public. We may want to surface those in a different way
    // .filter(e => !hasPublicAccess(e.acls))
    .sort((a, b) => (a.name ?? '').localeCompare(b?.name ?? ''))
  const spacesIds = new Set([...spaces.map(r => r.id)])

  const graph = graphHooks.useEntityGraph()

  const addSpace = async (ev: MouseEvent) => {
    ev.preventDefault()

    const space = await graph
      .createEntity({ parent: null, entity: { type: 'folder', name: 'New Space', tags: ['space'] } })
      .unwrap()
    if (space) {
      navigateToEntity(space)
    }
  }
  const busyCreating = graph.createEntityResult.isLoading

  const inbox = ownedEntities.filter(r => !spacesIds.has(r.id))

  const plugins = usePlugins('all')
  const mimeTypes = getAcceptedMimeTypes(plugins)

  return (
    <ContentContainer>
      <Typography className={styles.header} variant="h2">
        Recent Activity
      </Typography>
      <RecentActivity type="grid" size="medium" />
      <div className={styles.spacesWrapper}>
        <Typography className={cn(styles.header, styles.headerSpaces)} variant="h2">
          Spaces
        </Typography>
        <Button
          disabled={busyCreating}
          onClick={addSpace}
          startIcon={<Add />}
          className={styles.addButton}
          color="primary"
          data-testid="create-new-space"
        >
          New Space
        </Button>
      </div>
      <EntityGrid
        size={'large'}
        skeletonItems={9}
        entities={spaces}
        loading={isLoadingEntities || searching}
        onOpen={navigateToEntity}
        placeholder="No spaces found"
      />
      <Typography className={styles.header} variant="h2">
        Inbox
      </Typography>
      <DndList
        size="large"
        isLoading={isLoadingEntities || searching}
        entities={inbox}
        optionsKey={'root'}
        placeholder="No items in Inbox"
      />
      <div className={styles.spacer} />
      <div className={styles.fabContainer}>
        <RecordFabButton position="relative" color="primary" />
      </div>
      <DropZone parentId={null} accept={mimeTypes} uploadOnClick showClickZone showZone />
    </ContentContainer>
  )
}

export default RootMain
